<script setup lang="ts">
/**
 * One CV, two outputs: the PDF laid over the personal website it also
 * publishes. Sits under the live counters so the hero stays focused on the
 * generator input.
 */
const { t } = useI18n()
</script>

<template>
  <section class="sofax overflow-hidden bg-white dark:bg-gray-950">
    <div class="section-space">
      <div class="shell relative">
        <SofaxSectionHeading
          :eyebrow="t('homepage.outputShowcase.eyebrow')"
          :title="t('homepage.outputShowcase.title')"
          :text="t('homepage.outputShowcase.text')"
        />

      <div class="relative mx-auto max-w-5xl pb-8 md:pb-14">
        <!-- Website in a browser frame -->
        <div class="relative ml-auto w-[88%] md:w-[82%]">
          <div
            class="absolute -top-4 right-3 z-20 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-accent-strong px-3 py-1.5 !text-xs font-semibold !leading-none !text-white shadow-lift md:-top-5 md:right-6 md:px-4 md:py-2.5 md:!text-sm"
          >
            <i class="bi bi-globe2" />
            {{ t('homepage.hero.visual.web') }}
          </div>

          <div class="overflow-hidden rounded-[0.625rem] border border-line bg-white shadow-lift dark:border-gray-700 dark:bg-gray-800">
            <div class="flex items-center gap-3 border-b border-line bg-gray-50 px-3 py-2 md:px-4 md:py-2.5 dark:border-gray-700 dark:bg-gray-900">
              <div class="flex shrink-0 gap-1.5" aria-hidden="true">
                <span class="size-2 rounded-full bg-red-400 md:size-2.5" />
                <span class="size-2 rounded-full bg-yellow-400 md:size-2.5" />
                <span class="size-2 rounded-full bg-green-400 md:size-2.5" />
              </div>
              <div
                class="mx-auto flex min-w-0 max-w-sm flex-1 items-center justify-center gap-1.5 truncate rounded-md bg-white px-3 py-1 text-[0.625rem] text-muted md:text-xs dark:bg-gray-800 dark:text-gray-400"
              >
                <i class="bi bi-lock-fill text-green-600 dark:text-green-400" />
                <span class="truncate">inmyresume.com/cv/<span class="font-semibold text-ink dark:text-white">alexandra-johnson</span></span>
              </div>
              <span class="w-8 shrink-0 md:w-10" aria-hidden="true" />
            </div>
            <NuxtImg
              src="/web-sofax.png"
              :alt="t('homepage.hero.visual.webAlt')"
              format="webp"
              quality="85"
              width="820"
              height="669"
              sizes="xs:340px md:820px"
              class="aspect-[16/10] w-full object-cover object-top"
              loading="eager"
            />
          </div>
        </div>

        <!-- PDF CV sheet in front -->
        <div class="absolute bottom-0 left-0 z-10 w-[36%] -rotate-3 md:w-[27%]">
          <div
            class="absolute -top-4 left-1/2 z-20 inline-flex -translate-x-1/2 items-center gap-2 whitespace-nowrap rounded-full bg-ink px-3 py-1.5 !text-xs font-semibold !leading-none !text-white shadow-lift md:-top-5 md:px-4 md:py-2.5 md:!text-sm dark:bg-white dark:!text-ink"
          >
            <i class="bi bi-file-earmark-pdf" />
            {{ t('homepage.hero.visual.pdf') }}
          </div>
          <div class="overflow-hidden rounded-[0.625rem] border border-line bg-white shadow-lift dark:border-gray-700">
            <NuxtImg
              src="/modern.png"
              :alt="t('homepage.hero.visual.pdfAlt')"
              format="webp"
              quality="90"
              width="280"
              height="395"
              sizes="xs:150px md:280px"
              class="w-full"
              loading="eager"
            />
          </div>
        </div>
        <img
          src="/sofax/hero-1-element-2.svg"
          alt=""
          aria-hidden="true"
          width="311"
          height="243"
          class="pointer-events-none absolute -right-24 -top-16 -z-10 hidden opacity-70 2xl:inline-block"
        >
      </div>
      </div>
    </div>
  </section>
</template>
